import { actions, raise, sendTo } from "xstate"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { RequestResponseChannel } from "../../../../../_common/machines/data-load-machine"
import { ECommerceEvent, ECommerceMachineOptions, ECommerceStateNodeConfig } from "../e-commerce-machine"
import { ECommerceContext } from "../e-commerce-machine-context"
import {
    buildFareSuitePricesLoader,
    FareSuitePricesLoaderActorRef,
    fareSuitePricesLoaderId,
} from "../data-loading/fare-suite-prices-loader-machine"
import { assertEvent } from "../../../../../_common/xstate-helpers"
import { maybeFareCardByIndex } from "../../../read-models/fare"
import { validVsCodeValue, vsMember } from "../../../vs-code"
import { FareCard, FareCardIndex } from "../../../read-models/fare-card"
import { isNotCheckingOut } from "./common"

const availableIndexes = [0, 1, 2, 3].filter(FareCardIndex.is)

export const allFareSuitePricesState = (websocketClient: RequestResponseChannel): ECommerceStateNodeConfig => ({
    type: "parallel",
    states: availableIndexes
        .map((index) => ({
            [fareSuitePricesLoaderStateId(index)]: fareSuitePricesLoaderState(websocketClient, index),
        }))
        .reduce((s, a) => Object.assign(s, a), {}),
    on: {
        FARE_SUITE_PRICES_LOADED: { actions: ["updateFareCardWithSuitePrices"] },
        FARE_SUITE_PRICES_ERROR: { actions: ["updateFareCardAsError"] },
    },
})

type Context = ECommerceContext
type Events = ECommerceEvent
type Options = Required<Pick<ECommerceMachineOptions, "actions" | "guards">>

const fareSuitePricesLoaderState = (
    websocketClient: RequestResponseChannel,
    index: FareCardIndex,
): ECommerceStateNodeConfig => ({
    id: fareSuitePricesLoaderStateId(index),
    invoke: {
        id: fareSuitePricesLoaderId(index),
        src: buildFareSuitePricesLoader(websocketClient, index),
    },
    on: {
        LOAD_FARE_SUITE_PRICES: {
            cond: loadFareSuitePricesEventHasIndex(index),
            actions: ["setFareCardLoading", sendLoadForCardAt(index)],
        },
        FARE_CARDS_LOADED: { cond: fareIndexExist(index), actions: [raiseLoadFareSuitePrices(index)] },
        FARE_CARD_CHANGED: {
            cond: (_, ev) => ev.fareCard.index === index,
            actions: [raiseLoadFareSuitePrices(index)],
        },
        VS_CODE_CHANGED: { cond: fareIndexExistInContext(index), actions: [raiseLoadFareSuitePrices(index)] },
    },
})

const raiseLoadFareSuitePrices = (index: number) => `raiseLoadFareSuitePrices${index}`
const fareIndexExist = (index: number) => `fareIndexExist${index}`
const fareIndexExistInContext = (index: number) => `fareIndexExistInContext${index}`
const loadFareSuitePricesEventHasIndex = (index: number) => `loadFareSuitePricesEventHasIndex${index}`
const sendLoadForCardAt = (index: number) => `sendLoadForCardAt${index}`

const fareSuitePricesLoaderOptions = (index: FareCardIndex): Options => ({
    actions: {
        [raiseLoadFareSuitePrices(index)]: raise<Context, Events>({ type: "LOAD_FARE_SUITE_PRICES", index }),
        [sendLoadForCardAt(index)]: actions.pure((ctx) =>
            F.pipe(
                maybeFareCardByIndex(ctx, index),
                O.foldW(F.constUndefined, (fareCard) =>
                    sendTo<Context, Events, FareSuitePricesLoaderActorRef>(
                        fareSuitePricesLoaderId(index),
                        (ctx, ev) => {
                            assertEvent(ev, "LOAD_FARE_SUITE_PRICES")
                            return {
                                type: "LOAD",
                                data: fareSuitePricesRequest(ctx, fareCard),
                                country: ctx.geoLocation.countryCode,
                            }
                        },
                    ),
                ),
            ),
        ),
    },
    guards: {
        [fareIndexExist(index)]: (_, ev) => {
            assertEvent(ev, "FARE_CARDS_LOADED")
            return ev.fareCards.some((x) => x.index === index)
        },
        [loadFareSuitePricesEventHasIndex(index)]: (ctx, ev, meta) => {
            assertEvent(ev, "LOAD_FARE_SUITE_PRICES")
            return ev.index === index && isNotCheckingOut(ctx, ev, meta)
        },
        [fareIndexExistInContext(index)]: (ctx) => ctx.fareCards.find((x) => x.index === index) !== undefined,
    },
})

const fareSuitePricesRequest = (ctx: Context, fareCard: FareCard) => ({
    cruiseCode: ctx.cruiseCode,
    occupancy: ctx.guests,
    fareCode: fareCard.fare,
    vsMember: vsMember(ctx.vsCode, fareCard.options.vs),
    vsCode: O.fromNullable(validVsCodeValue(ctx.vsCode)),
    availabilities: ctx.flowVersion.availabilities,
    air: fareCard.air,
})

const emptyOptions: Options = {
    actions: {},
    guards: {},
}

export const allFareSuitePricesOptions: Options = availableIndexes
    .map(fareSuitePricesLoaderOptions)
    .reduce((p, c) => ({ actions: { ...p.actions, ...c.actions }, guards: { ...p.guards, ...c.guards } }), emptyOptions)

const fareSuitePricesLoaderStateId = (index: number): string => `${fareSuitePricesLoaderId(index)}-state`
